import { Component, ElementRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { EventEmitter } from 'events';
import { fromEvent } from 'rxjs';
import { pairwise, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-digital-signature',
  templateUrl: './digital-signature.component.html',
  styleUrls: ['./digital-signature.component.scss']
})
export class DigitalSignatureComponent implements OnInit {

  constructor() { }

  ngOnInit() {
  }

  // a reference to the canvas element from our template
  @ViewChild('canvas', { static: true }) public canvas: ElementRef;
  @ViewChild('canvas_container', { static: true }) public canvas_container: ElementRef;

  // setting a width and height for the canvas
  @Input() public width = 400;
  @Input() public height = 400;
  @Input() public label = "Digital Signature";
  @Input() public placeholder = "Please sign here using mouse/finger/stylus/digital pen";

  @Input() type;
  @Input() value;
  @Input() id;
  @Input() style;
  @Input() class;
  @Input() hint;
  @Input() control_type;
  @Input() required;
  @Input() error_msg;
  @Input() controlName;
  @Input() ngModel;
  @Input() verified_status;
  @Input() notes;
  @Input() reason;
  @Input() verification_required;
  @Input() hide_label;

  @Output() getValue = new EventEmitter();
  @Input() label_block;
  @Input() context_menu_listner;
  @Input() on_change_listner;
  @Input() html_decode;
  @Input() disabled;
  @Input() enable_manual_upload;

  @Input() form_group;
  @Input() form_control;

  private cx: CanvasRenderingContext2D;

  public ngAfterViewInit() {
    // get the context
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
    this.cx = canvasEl.getContext('2d');

    // set the width and height

    let width = getComputedStyle(this.canvas_container.nativeElement).getPropertyValue('width')
    let height = getComputedStyle(this.canvas_container.nativeElement).getPropertyValue('height')
    canvasEl.width = parseInt(width) - 20 || 400;
    canvasEl.height = parseInt(height) - 20 || 400;

    // set some default properties about the line
    this.cx.lineWidth = 3;
    this.cx.lineCap = 'round';
    this.cx.strokeStyle = '#000';

    // we'll implement this method to start capturing mouse events
    this.captureEvents(canvasEl);
  }

  private captureEvents(canvasEl: HTMLCanvasElement) {
    // this will capture all mousedown events from the canvas element
    fromEvent(canvasEl, 'mousedown')
      .pipe(
        switchMap((e) => {
          // after a mouse down, we'll record all mouse moves
          return fromEvent(canvasEl, 'mousemove')
            .pipe(
              // we'll stop (and unsubscribe) once the user releases the mouse
              // this will trigger a 'mouseup' event
              takeUntil(fromEvent(canvasEl, 'mouseup')),
              // we'll also stop (and unsubscribe) once the mouse leaves the canvas (mouseleave event)
              takeUntil(fromEvent(canvasEl, 'mouseleave')),
              // pairwise lets us get the previous value to draw a line from
              // the previous point to the current point
              pairwise()
            )
        })
      )
      .subscribe((res: [MouseEvent, MouseEvent]) => {
        const rect = canvasEl.getBoundingClientRect();

        // previous and current position with the offset
        const prevPos = {
          x: res[0].clientX - rect.left,
          y: res[0].clientY - rect.top
        };

        const currentPos = {
          x: res[1].clientX - rect.left,
          y: res[1].clientY - rect.top
        };

        // this method we'll implement soon to do the actual drawing
        this.drawOnCanvas(prevPos, currentPos);
      });
  }

  private drawOnCanvas(
    prevPos: { x: number, y: number },
    currentPos: { x: number, y: number }
  ) {
    // incase the context is not set
    if (!this.cx) { return; }

    // start our drawing path
    this.cx.beginPath();

    // we're drawing lines so we need a previous position
    if (prevPos) {
      // sets the start point
      this.cx.moveTo(prevPos.x, prevPos.y); // from

      // draws a line from the start pos until the current position
      this.cx.lineTo(currentPos.x, currentPos.y);

      // strokes the current path with the styles we set earlier
      this.cx.stroke();
      var jpegUrl = this.canvas.nativeElement.toDataURL("image/jpeg");
      var pngUrl = this.canvas.nativeElement.toDataURL();
      this.form_group.get(this.form_control).setValue(pngUrl);
    }
  }

  clearCanvas() {
    const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
    let width = getComputedStyle(this.canvas_container.nativeElement).getPropertyValue('width')
    let height = getComputedStyle(this.canvas_container.nativeElement).getPropertyValue('height')
    canvasEl.width = parseInt(width) - 20 || 400;
    canvasEl.height = parseInt(height) - 20 || 400;
    this.cx.lineWidth = 3;
    this.cx.lineCap = 'round';
    this.cx.strokeStyle = '#000';
    this.cx.clearRect(0, 0, canvasEl.width, canvasEl.height);
    this.form_group.get(this.form_control).setValue(null);
  }
}
